import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IDistrictMainPageTrackingProps} from 'app/components/district/news/DistrictMainPageTracking';

const useSwipeableViewsActiveStep = (
  stepNumber: number,
  trackDataCallback?: (data: IDistrictMainPageTrackingProps) => void
) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const maxSteps = useMemo(() => stepNumber - 1, [stepNumber]);
  const maxActiveStepIndex = useRef(0);
  const touched = useRef(false);

  useEffect(() => {
    return () => {
      if (touched.current) {
        trackDataCallback?.({
          viewedPageIndex: maxActiveStepIndex.current,
          totalPagesCount: stepNumber,
        });
      }
    };
  }, [stepNumber, trackDataCallback]);

  const updateActiveStep = useCallback(
    (newActiveStep: number) => {
      if (newActiveStep < 0) {
        return;
      }
      if (newActiveStep > maxSteps) {
        return;
      }
      setActiveStep(newActiveStep);
      if (newActiveStep > maxActiveStepIndex.current) {
        maxActiveStepIndex.current = newActiveStep;
      }
      if (!touched.current) {
        touched.current = true;
      }
    },
    [maxSteps]
  );

  const handleNext = useCallback(() => {
    updateActiveStep(activeStep + 1);
  }, [activeStep, updateActiveStep]);

  const handleBack = useCallback(() => {
    updateActiveStep(activeStep - 1);
  }, [activeStep, updateActiveStep]);

  const handleIndexChange = useCallback(
    (index: number) => {
      updateActiveStep(index);
    },
    [updateActiveStep]
  );

  return {activeStep, maxSteps, handleNext, handleBack, handleIndexChange};
};

export default useSwipeableViewsActiveStep;
